import { ChangeTyped } from '@damofire/data-commons';
import {
  Bay,
  FirebaseIssueCfgService,
  Issue,
  LayoutTextSegment,
  Repair,
  System2d,
  SystemNumberOrientation,
  WarehouseOutlineSegment,
  WarehouseSectionBounds,
} from '@damofire/data-watson';
import { Bounds, CanvasCoordinates, CanvasDisplayPrecision, CanvasZoomState, EmitterEvent, inBounds, screenToCanvas, updateBounds } from '@damofire/uix-commons';
import { isEqual } from 'lodash-es';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import { WatsonCanvasDecorator } from '../../watson-canvas.decorator';
import { BAY_CAPACITY_COLOR_CODES, DEFAULT_BAY_CFG, DEFAULT_ISSUE_CFG, DEFAULT_REPAIR_CFG } from './canvas.cfg';
import { CapacityClass, LayoutBay } from './layout-components';
import { IssueLayoutRenderer, LayoutIssues } from './layout-issue.renderer';
import { OutlineLayoutRenderer } from './layout-outline.renderer';
import { LayoutRepairs, RepairLayoutRenderer } from './layout-repair.renderer';
import { SystemLayoutRenderer } from './layout-system.renderer';
import { LayoutTextRenderer } from './layout-text.renderer';
import { LayoutDisplayMode, WarehouseLayoutModel } from './warehouse-layout.model';
import { SectionRenderer } from './section.renderer';
import { environment } from '../../../../environments/environment';
import { filterLayoutIssue } from './layout-issue-filter.utils';

export class LayoutCanvasDecorator extends WatsonCanvasDecorator {
  bayClicked = new Subject<EmitterEvent>();
  issueClicked = new Subject<EmitterEvent>();
  repairClicked = new Subject<EmitterEvent>();
  sectionClicked = new Subject<EmitterEvent>();

  private drawIssues = true;
  private drawRepairs = false;

  private model: WarehouseLayoutModel;
  protected systemRenderers: { [system: string]: SystemLayoutRenderer } = {};
  protected layoutTextRenderers: { [textId: string]: LayoutTextRenderer } = {};
  private issueRenderers: { [assetId: string]: IssueLayoutRenderer } = {};
  private repairRenderers: { [assetId: string]: RepairLayoutRenderer } = {};
  private sectionRenderers: SectionRenderer[] = [];
  private sections: WarehouseSectionBounds[] = [];
  private outlineRenderers: OutlineLayoutRenderer[] = [];

  protected assetIds: string[] = [];
  protected bays: { [assetId: string]: LayoutBay } = {};
  protected issues: { [assetId: string]: LayoutIssues } = {};
  protected repairs: { [assetId: string]: LayoutRepairs } = {};

  private selectedBay: LayoutBay;
  private selectedIssue: LayoutIssues;
  private selectedRepair: LayoutRepairs;
  private hoveredSection: string | null = null;

  protected outlineLock: Promise<boolean>;
  private readonly outlineSub: Subject<boolean> = new Subject();

  protected baysLock: Promise<boolean>;
  private readonly baysSub: Subject<boolean> = new Subject();

  private hoverSub: Subscription;

  private static afterRotation(p1: CanvasCoordinates, o: CanvasCoordinates, angle: number): CanvasCoordinates {
    const toOriginX = p1.x - o.x;
    const toOriginY = p1.y - o.y;

    const x = toOriginX * Math.cos(angle) - toOriginY * Math.sin(angle);
    const y = toOriginX * Math.sin(angle) + toOriginY * Math.cos(angle);

    return { x: x + o.x, y: y + o.y };
  }

  private static unlocker<T>(sub: Observable<T>, label: string): Promise<T> {
    return new Promise<T>((resolve) => {
      const subscription = sub.subscribe({
        next: (value: T) => {
          console.log(`Unlocker (${label}) got a new value: ${value}`);
          if (value) {
            subscription?.unsubscribe();
            resolve(value);
          }
        },
      });
    });
  }

  private static toBay(matchingBay: LayoutBay): Bay {
    return { leftAssetId: matchingBay.upright.assetId, rightAssetId: matchingBay.nextUpright.assetId };
  }

  private static formatSystemsAsRanges(systems: number[]): string {
    if (!systems?.length) {
      return '-';
    }

    const numbers = [...systems].filter((n) => !isNaN(n)).sort((a, b) => a - b);

    if (!numbers.length) {
      return '-';
    }

    const ranges: string[] = [];
    let rangeStart = numbers[0];
    let rangeEnd = numbers[0];

    for (let i = 1; i < numbers.length; i++) {
      if (numbers[i] === rangeEnd + 1) {
        rangeEnd = numbers[i];
      } else {
        ranges.push(rangeStart === rangeEnd ? `[${rangeStart}]` : `[${rangeStart}-${rangeEnd}]`);
        rangeStart = numbers[i];
        rangeEnd = numbers[i];
      }
    }
    ranges.push(rangeStart === rangeEnd ? `[${rangeStart}]` : `[${rangeStart}-${rangeEnd}]`);

    return ranges.join(', ');
  }

  constructor(
    protected readonly canvas: HTMLCanvasElement,
    protected readonly enableMouseHandler = false,
    protected readonly debugInfos = false,
  ) {
    super(canvas, enableMouseHandler, debugInfos);
    this.outlineLock = LayoutCanvasDecorator.unlocker(this.outlineSub, 'outlines');
    this.baysLock = LayoutCanvasDecorator.unlocker(this.baysSub, 'bays');

    // Subscribe to mouse move events to handle section hover
    if (enableMouseHandler) {
      this.hoverSub = this.mouseMoved.pipe(debounceTime(50)).subscribe((event) => {
        const target = this.searchPossibleTargets(event.extras.eventCoords);
        const newHoveredSection = target?.type === 'LayoutSection' ? (target.target as WarehouseSectionBounds).name : null;

        if (this.hoveredSection !== newHoveredSection) {
          this.hoveredSection = newHoveredSection;
          this.renderAll();
        }
      });
    }
  }

  destroy(): void {
    super.destroy();

    this.hoverSub?.unsubscribe();

    this.model = null;
    this.systemRenderers = null;
    this.layoutTextRenderers = null;
    this.issueRenderers = null;
    this.repairRenderers = null;
    this.sectionRenderers = null;
    this.outlineRenderers = null;

    this.assetIds = null;
    this.bays = null;
    this.issues = null;

    this.selectedBay = null;
    this.selectedIssue = null;
  }

  updateLayout(zoomState?: CanvasZoomState): void {
    if (!this.destroyed) {
      if (zoomState?.renderingCtx) {
        console.log(`Restoring zoom state.`, zoomState);
        this.renderingCtx = { ...zoomState.renderingCtx };
        this.defaultRenderingCtx = { ...zoomState.renderingCtx };
      } else {
        this.resetZoom(false);
      }

      this.renderAll();
    }
  }

  updateLanguage(): void {
    // À faire dans la story des tooltips
  }

  zoomToTarget(): void {
    if (this.selectedBay) {
      this.zoomOnObject(this.selectedBay);
    }

    if (this.selectedIssue) {
      this.zoomOnObject(this.selectedIssue);
    }
  }

  zoomOnAsset(assetId: string): Bay {
    const matchingBay = this.selectAsset(assetId, false);
    this.zoomOnObject(matchingBay);

    return matchingBay ? LayoutCanvasDecorator.toBay(matchingBay) : null;
  }

  selectTarget(
    target: { type: 'LayoutBay' | 'LayoutIssue' | 'LayoutRepair' | 'LayoutSection'; target: LayoutBay | LayoutIssues | LayoutRepairs | WarehouseSectionBounds },
    coordinates: CanvasCoordinates,
  ): void {
    switch (target.type) {
      case 'LayoutSection':
        this.selectSection((target.target as WarehouseSectionBounds).name, true);
        break;

      case 'LayoutIssue':
        this.selectIssue((target.target as LayoutIssues).assetId, true);
        break;

      case 'LayoutRepair':
        this.selectRepair((target.target as LayoutRepairs).assetId, true);
        break;

      default:
        this.selectAsset((target.target as LayoutBay).upright.assetId, true);
        break;
    }
  }

  selectSection(sectionName: string, triggerEvent = false): void {
    console.log(`Section clicked: ${sectionName}`);
    if (triggerEvent) {
      this.sectionClicked.next({ value: sectionName } as EmitterEvent);
    }
  }

  selectAsset(assetId: string, triggerEvent = false): LayoutBay {
    this.clearSelection(false);
    if (!this.bays[assetId]) {
      console.log(`Did not find any matching bay, cannot update selection.`);
      return null;
    }

    if (this.bays[assetId]?.upright.assetId !== assetId) {
      this.bays[assetId].upright.assetId = this.bays[assetId].nextUpright.assetId;
      this.bays[assetId].nextUpright.assetId = null;
    }
    this.selectedBay = this.bays[assetId];

    if (this.selectedBay) {
      console.log(`Found a matching bay, updating selection.`);
      const assetSystem = this.systemRenderers[this.selectedBay.system];
      assetSystem.selectedAsset = assetId;

      if (triggerEvent) {
        this.bayClicked.next({ value: LayoutCanvasDecorator.toBay(this.selectedBay), extras: this.selectedBay });
      }
    } else {
      console.log(`Did not find any matching bay, cannot update selection.`);
    }

    this.renderAll();
    return this.selectedBay;
  }

  selectIssue(assetId: string, triggerEvent = false): void {
    this.clearSelection(false);

    this.selectedIssue = this.issues[assetId];
    if (this.selectedIssue) {
      console.log(`Found a matching issue, updating selection.`);
      this.issueRenderers[this.selectedIssue.assetId].selected = true;
    }

    this.renderAll();

    if (triggerEvent) {
      this.issueClicked.next({ value: this.selectedIssue?.assetId } as EmitterEvent);
    }
  }

  selectRepair(assetId: string, triggerEvent = false): void {
    this.clearSelection(false);

    this.selectedRepair = this.repairs[assetId];
    if (this.selectedRepair) {
      console.log(`Found a matching repair, updating selection.`);
      this.repairRenderers[this.selectedRepair.assetId].selected = true;
    }

    this.renderAll();

    if (triggerEvent) {
      this.repairClicked.next({ value: this.selectedRepair?.assetId } as EmitterEvent);
    }
  }

  clearSelection(render = true): void {
    if (this.destroyed) {
      return;
    }

    console.log(`Clearing selection.`, this.selectedIssue, this.selectedBay);

    if (this.selectedIssue) {
      const renderer = this.issueRenderers[this.selectedIssue.assetId];
      if (renderer) {
        renderer.selected = false;
      }
    }

    if (this.selectedRepair) {
      const renderer = this.repairRenderers[this.selectedRepair.assetId];
      if (renderer) {
        renderer.selected = false;
      }
    }

    if (this.selectedBay) {
      const renderer = this.systemRenderers[this.selectedBay.system];
      if (renderer) {
        renderer.selectedAsset = null;
      }
    }

    this.selectedBay = null;
    this.selectedIssue = null;
    this.selectedRepair = null;

    if (render) {
      this.renderAll();
    }
  }

  setLayoutDisplayMode(mode: LayoutDisplayMode): void {
    switch (mode) {
      case LayoutDisplayMode.CAPACITY:
        this.hideAllRepairs();
        this.hideAllIssues();
        this.showCapacities();
        break;

      case LayoutDisplayMode.REPAIRS:
        this.drawRepairs = true;
        this.hideAllIssues();
        this.hideCapacities();
        break;

      case LayoutDisplayMode.ISSUES:
      default:
        this.drawIssues = true;
        this.hideCapacities();
        this.hideAllRepairs();
        this.filterIssues(false);
        break;
    }

    this.renderAll();
  }

  filterIssues(render = true): void {
    if (!this.destroyed) {
      console.log(`Filtering issues on layout. [p:${this.model.priorityFilter}, c:${this.model.componentFilter}]`);

      for (const id of this.assetIds) {
        const renderer = this.issueRenderers[id];
        if (renderer) {
          this.updateExistingIssueGroupWithFilteredIssues(renderer);
        }
      }

      console.log(`Done filtering issues.`);
      if (render) {
        this.renderAll();
      }
    }
  }

  hideAllIssues(): void {
    this.drawIssues = false;
    if (this.selectedIssue) {
      this.clearSelection(false);
    }
  }

  hideAllRepairs(): void {
    this.drawRepairs = false;
    if (this.selectedRepair) {
      this.clearSelection(false);
    }
  }

  showCapacities(): void {
    console.log(`Showing bay capacities.`);
    const start = new Date().getTime();

    Object.values(this.systemRenderers).forEach((s) => (s.displayCapacities = true));

    const end = new Date().getTime();
    console.log(`Showing capacities took ${end - start}ms.`);
  }

  hideCapacities(): void {
    console.log(`Hiding bay capacities.`);
    const start = new Date().getTime();

    Object.values(this.systemRenderers).forEach((s) => (s.displayCapacities = false));

    const end = new Date().getTime();
    console.log(`Hiding capacities took ${end - start}ms.`);
  }

  async updateIssuesSnap(issues: ChangeTyped<Issue>[]): Promise<void> {
    if (!this.destroyed) {
      console.log(`Waiting for bays to be drawn before updating issues.`);
      await this.baysLock;
      const begin = new Date().getTime();
      console.log(`Update issues process started.`, issues);

      issues.forEach((i: ChangeTyped<Issue>) => this.updateIssueSnap(i));
      this.renderAll();

      const end = new Date().getTime();
      console.log(`Issues updated in ${end - begin}ms.`);
    }
  }

  async updateRepairsSnap(repairs: ChangeTyped<Repair>[]): Promise<void> {
    if (!this.destroyed) {
      console.log(`Waiting for bays to be drawn before updating repairs.`);
      await this.baysLock;
      const begin = new Date().getTime();
      console.log(`Update repairs process started.`, repairs);

      repairs.forEach((i: ChangeTyped<Repair>) => this.updateRepairSnap(i));
      this.renderAll();

      const end = new Date().getTime();
      console.log(`Repairs updated in ${end - begin}ms.`);
    }
  }
  async waitForBays(): Promise<void> {
    await this.baysLock;
  }

  removeRepairs(repairs: Repair[]): void {
    if (!repairs) {
      return;
    }

    for (const repair of repairs) {
      const matching = this.repairRenderers[`${repair.upright._id}`];
      if (matching) {
        this.removeRepair(matching, repair);
      }
    }

    this.renderAll();
  }

  removeIssues(issues: Issue[]): void {
    if (!issues) {
      return;
    }

    for (const i of issues) {
      const matching = this.issueRenderers[`${i.asset._id}`];
      if (matching) {
        this.removeIssue(matching, i);
      }
    }

    this.renderAll();
  }

  removeAllIssues(): void {
    if (!this.destroyed) {
      console.log(`Removing all issues from canvas.`);
      this.clearSelection();
      this.issueRenderers = {};
      this.renderAll();
    } else {
      console.log("Canvas is destroyed, can't remove all issues.");
    }
  }

  async updateSystems(systems: System2d[], capacitiesMode = false): Promise<void> {
    if (!this.destroyed) {
      console.log(`Updating canvas with ${systems?.length || 0} systems.`);
      return this.outlineLock.then(() => {
        this.systemRenderers = {};
        // If no section is defined, we render all systems
        for (const s of systems) {
          if (s.uprights) {
            const renderer = this.createSystem(this.ctx, s, capacitiesMode);
            this.systemRenderers[`S${s.number}`] = renderer;
            renderer.layoutBays.forEach((b, inx) => {
              this.assetIds.push(b.upright.assetId);
              this.bays[b.upright.assetId] = b;

              if (inx === renderer.layoutBays.length - 1) {
                this.assetIds.push(b.nextUpright.assetId);
                this.bays[b.nextUpright.assetId] = b;
              }

              if (!inBounds(this.renderedBounds, { x: b.x, y: b.y })) {
                updateBounds(this.renderedBounds, { x: b.x, y: b.y });
              }
            });
          } else {
            console.warn('System has no uprights.', s);
          }
        }

        this.renderAll();
        this.baysSub.next(true);
      });
    }

    return Promise.resolve();
  }

  createSystem(ctx: CanvasRenderingContext2D, system: System2d, showCapacities = false): SystemLayoutRenderer {
    let angleRad = Math.atan2(system.yDirection, system.xDirection);
    if (angleRad < 0) {
      angleRad = 2 * Math.PI + angleRad;
    }

    const layoutBays: LayoutBay[] = [];

    for (let i = 1; i < system.uprights.length; ++i) {
      const previous = system.uprights[i - 1];
      const previousOrigin = { x: previous.x, y: previous.y };
      const uprightOrigin = { x: system.uprights[i].x, y: system.uprights[i].y };
      const width = this.distance(previousOrigin, uprightOrigin);
      const capacityClass = this.classForCapacity(previous.capacity);

      layoutBays.push({
        capacity: previous.capacity,
        capacityClass: capacityClass,
        x: Math.round(previous.x),
        y: Math.round(previous.y),
        w: width,
        h: system.depth,
        fill: showCapacities ? BAY_CAPACITY_COLOR_CODES[capacityClass] : DEFAULT_BAY_CFG.fill,
        angleRad,
        upright: previous,
        nextUpright: system.uprights[i],
        system: `S${system.number}`,
        aisle: system.aisle,
      } as LayoutBay);
    }

    const sumCapacities = layoutBays.reduce((cw, b) => cw + b.capacity, 0);
    const averageCapacity = sumCapacities / layoutBays.length;
    const averageCapacityClass = this.classForCapacity(averageCapacity);

    const w = layoutBays.reduce((cw, b) => cw + b.w, 0);
    const h = layoutBays[0]?.h;
    const showSystemNumbers = this.model?.warehouse?.showSystemNumbers ?? true;
    const systemNumberOrientation = this.model?.warehouse?.systemNumberOrientation ?? SystemNumberOrientation.DOWN_AISLE;
    return new SystemLayoutRenderer(
      ctx,
      system,
      layoutBays,
      angleRad,
      showCapacities,
      w,
      h,
      averageCapacityClass,
      showSystemNumbers,
      systemNumberOrientation,
    );
  }

  classForCapacity(capacity: number): CapacityClass {
    let capacityClass: CapacityClass = 'low';
    if (capacity >= 2500) {
      capacityClass = 'high';
    } else if (capacity >= 2000) {
      capacityClass = 'medium';
    }

    return capacityClass;
  }

  distance(p1: CanvasCoordinates, p2: CanvasCoordinates): number {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;

    return Math.round(Math.sqrt(dx * dx + dy * dy));
  }

  updateTexts(layoutTextSegments: LayoutTextSegment[]): void {
    if (this.destroyed) {
      return;
    }

    console.log(`Updating canvas with ${layoutTextSegments?.length || 0} texts.`);

    for (const layoutText of layoutTextSegments) {
      this.layoutTextRenderers[layoutText._id] = new LayoutTextRenderer(this.ctx, layoutText);

      const { xOrigin, yOrigin } = layoutText;
      if (!inBounds(this.renderedBounds, { x: xOrigin, y: yOrigin })) {
        updateBounds(this.renderedBounds, { x: xOrigin, y: yOrigin });
      }
    }

    this.renderAll();
  }

  clear(): void {
    this.renderedBounds = {
      minX: Number.MAX_VALUE,
      minY: Number.MAX_VALUE,
      maxX: -Number.MAX_VALUE,
      maxY: -Number.MAX_VALUE,
      width: 0,
      height: 0,
    };
    this.outlineRenderers = [];
    this.layoutTextRenderers = {};
    this.systemRenderers = {};
    this.issueRenderers = {};
    this.assetIds = [];
    this.bays = {};
    this.issues = {};

    this.outlineLock = LayoutCanvasDecorator.unlocker(this.outlineSub, 'outlines');
    this.baysLock = LayoutCanvasDecorator.unlocker(this.baysSub, 'bays');

    this.renderAll();
  }

  updateOutline(segments: WarehouseOutlineSegment[]): void {
    if (!this.destroyed) {
      const begin = new Date().getTime();
      console.log(`Updating warehouse outline. [${segments.length}]`);

      if (segments) {
        this.outlineRenderers = segments.map((s) => {
          s.vertices.forEach((p) => updateBounds(this.renderedBounds, p));
          return new OutlineLayoutRenderer(this.ctx, s);
        });
        this.resetZoom(false);
      }

      if (this.model) {
        this.updateBoundsInModel();
      }

      const end = new Date().getTime();
      console.log(`Done updating warehouse outline. [${end - begin}ms]`);
      this.outlineSub.next(true);
    }
  }

  updateModel(warehouseLayoutModel: WarehouseLayoutModel): void {
    if (!this.destroyed) {
      this.model = warehouseLayoutModel;
      this.updateBoundsInModel();
      this.drawIssues = this.model.displayMode === LayoutDisplayMode.ISSUES;
    }
  }

  renderComponents(): void {
    // Render section boxes for all sections except the selected one
    this.sectionRenderers = [];
    this.sections = [];
    const selectedSection = this.model?.section;

    // Use pre-calculated bounds from backend if available
    if (this.model?.warehouse?.sectionBounds?.length && environment.appConfig.watson.sectionLayoutMode && this.model.warehouse.sectionMode) {
      for (const sectionBound of this.model.warehouse.sectionBounds) {
        // Store all sections for click detection (even selected ones)
        this.sections.push(sectionBound);

        // Only render non-selected sections
        if (sectionBound.name === selectedSection || +selectedSection === -1) {
          continue;
        }
        const isHovered = this.hoveredSection === sectionBound.name;
        const systemsLabel = `${this.model.labels['sections.systems'] ?? 'Systems'}: ${LayoutCanvasDecorator.formatSystemsAsRanges(sectionBound.systems)}`;
        const aislesLabel = sectionBound.aisles?.length ? `${this.model.labels['sections.aisles'] ?? 'Aisles'}: ${sectionBound.aisles.join(', ')}` : undefined;
        this.sectionRenderers.push(new SectionRenderer(this.ctx, sectionBound.name, sectionBound, isHovered, systemsLabel, aislesLabel));
      }
    }

    this.sectionRenderers.forEach((r) => r.draw(this.renderingCtx));

    const layoutSystems = Object.values(this.systemRenderers);
    const layoutTexts = Object.values(this.layoutTextRenderers);

    this.outlineRenderers.forEach((o) => o.draw());
    layoutSystems.forEach((b) => b.draw(this.renderingCtx));
    layoutTexts.forEach((b) => b.draw());

    if (this.drawIssues) {
      const layoutIssues = Object.values(this.issueRenderers);
      layoutIssues.forEach((i) => i.draw(this.renderingCtx));
    }

    if (this.drawRepairs) {
      const layoutRepairs = Object.values(this.repairRenderers);
      layoutRepairs.forEach((r) => r.draw(this.renderingCtx));
    }
  }

  searchPossibleTargets(eventCoordinates: CanvasCoordinates): {
    type: 'LayoutBay' | 'LayoutIssue' | 'LayoutRepair' | 'LayoutSection';
    target: LayoutBay | LayoutIssues | LayoutRepairs | WarehouseSectionBounds;
  } {
    const selectedSection = this.model?.section;
    const onCanvasCoords = screenToCanvas(this.canvas, eventCoordinates, this.renderingCtx);
    if (!this.model || !inBounds(this.renderedBounds, onCanvasCoords)) {
      return null;
    }

    let matchingTarget: LayoutBay | LayoutIssues | LayoutRepairs = null;
    if ([LayoutDisplayMode.ISSUES, LayoutDisplayMode.REPAIRS].includes(this.model.displayMode)) {
      let layoutItems: LayoutIssues[] | LayoutRepairs[];

      if (this.model.displayMode === LayoutDisplayMode.ISSUES) {
        layoutItems = Object.values(this.issues);
        matchingTarget = layoutItems.find((i) => {
          if (selectedSection && selectedSection !== '-1') {
            const issueSection = i.issues?.[0]?.asset?.section;
            if (issueSection && issueSection !== selectedSection) {
              return false;
            }
          }

          const minX = i.x - i.radius;
          const minY = i.y - i.radius;
          const maxX = i.x + i.radius;
          const maxY = i.y + i.radius;
          const xWithinRange = minX < onCanvasCoords.x && maxX > onCanvasCoords.x;
          const yWithinRange = minY < onCanvasCoords.y && maxY > onCanvasCoords.y;

          return xWithinRange && yWithinRange;
        });
      } else {
        layoutItems = Object.values(this.repairs);
        matchingTarget = layoutItems.find((i) => {
          const minX = i.x - i.size;
          const minY = i.y - i.size;
          const maxX = i.x + i.size;
          const maxY = i.y + i.size;
          const xWithinRange = minX < onCanvasCoords.x && maxX > onCanvasCoords.x;
          const yWithinRange = minY < onCanvasCoords.y && maxY > onCanvasCoords.y;

          return xWithinRange && yWithinRange;
        });
      }
    }

    let type: 'LayoutBay' | 'LayoutIssue' | 'LayoutRepair' = 'LayoutBay';
    if (matchingTarget && 'issues' in matchingTarget) {
      type = 'LayoutIssue';
    } else if (matchingTarget && 'repairs' in matchingTarget) {
      type = 'LayoutRepair';
    }

    if (!matchingTarget && this.model.displayPrecision !== CanvasDisplayPrecision.MINIMAL) {
      const layoutBays = Object.values(this.bays);
      matchingTarget = layoutBays.find((b: LayoutBay) => {
        if (selectedSection && selectedSection !== '-1') {
          const baySection = b.upright?.section || b.nextUpright?.section;
          if (baySection && baySection !== selectedSection) {
            return false;
          }
        }

        const bayBounds = {
          minX: Number.MAX_VALUE,
          minY: Number.MAX_VALUE,
          maxX: -Number.MAX_VALUE,
          maxY: -Number.MAX_VALUE,
          width: 0,
          height: 0,
        } as Bounds;
        const start = b;
        const next = LayoutCanvasDecorator.afterRotation({ x: b.x + b.w, y: b.y }, b, b.angleRad);
        const startDepth = LayoutCanvasDecorator.afterRotation({ x: b.x, y: b.upright.y + b.h }, b, b.angleRad);
        const nextDepth = LayoutCanvasDecorator.afterRotation({ x: b.x + b.w, y: b.upright.y + b.h }, b, b.angleRad);
        updateBounds(bayBounds, start, next, startDepth, nextDepth);

        return inBounds(bayBounds, onCanvasCoords);
      });
    }

    // Check for section clicks last (lowest priority - only if nothing else was clicked)
    if (!matchingTarget) {
      const sectionTarget = this.findMatchingSection(onCanvasCoords);
      if (sectionTarget) {
        return sectionTarget;
      }
    }

    return matchingTarget ? { target: matchingTarget, type } : null;
  }

  private findMatchingSection(onCanvasCoords: CanvasCoordinates): { type: 'LayoutSection'; target: WarehouseSectionBounds } | null {
    if (!this.sections.length || this.model.section === '-1') {
      return null;
    }

    const matchingSection = this.sections.find((section) => {
      if (section.name === this.model.section) {
        return false;
      }
      const { angle, cx, cy, width, height, minX, maxX, minY, maxY } = section;
      if (angle != null && cx != null && cy != null) {
        const dx = onCanvasCoords.x - cx;
        const dy = onCanvasCoords.y - cy;
        const rad = -(angle * Math.PI) / 180;
        const localX = dx * Math.cos(rad) - dy * Math.sin(rad);
        const localY = dx * Math.sin(rad) + dy * Math.cos(rad);
        return Math.abs(localX) <= width / 2 && Math.abs(localY) <= height / 2;
      }
      const xWithinRange = minX < onCanvasCoords.x && maxX > onCanvasCoords.x;
      const yWithinRange = minY < onCanvasCoords.y && maxY > onCanvasCoords.y;
      return xWithinRange && yWithinRange;
    });

    return matchingSection ? { type: 'LayoutSection', target: matchingSection } : null;
  }

  private createIssueGroup(matchingBay: LayoutBay, issue: Issue): IssueLayoutRenderer {
    if (!matchingBay) {
      console.warn(`No bay found for issue with asset ID: ${issue?.asset?._id}`);
      return null;
    }

    let top = matchingBay.y;
    let left = matchingBay.x;

    if (`${issue.asset._id}` === matchingBay.nextUpright.assetId) {
      top = matchingBay.nextUpright.y;
      left = matchingBay.nextUpright.x;
    }

    return this.createIssue(left, top, issue, this.model);
  }

  private createIssue(left: number, top: number, issue: Issue, warehouseLayoutModel: WarehouseLayoutModel): IssueLayoutRenderer {
    const matchesFilters = filterLayoutIssue(issue, warehouseLayoutModel);
    const fill = issue.riskLevel?.description ? warehouseLayoutModel.issueCfg.colors[issue.riskLevel.description.toUpperCase()] : '#ccc';

    const filteredIssues = matchesFilters ? [issue] : [];
    return new IssueLayoutRenderer(this.ctx, { x: left, y: top, issues: [issue], assetId: `${issue.asset._id}`, filteredIssues, fill, radius: DEFAULT_ISSUE_CFG.radius });
  }

  private updateIssueSnap(issueDetails: ChangeTyped<Issue>): void {
    const asset = issueDetails.asset;

    let matchingIssueRenderer = this.issueRenderers[`${asset._id}`];
    const shouldDisplay = issueDetails.changeType !== 'removed' && (issueDetails.resolution === 'OPEN' || this.model.inspection);

    if (!matchingIssueRenderer && shouldDisplay) {
      const matchingBay = this.bays[`${asset._id}`];
      matchingIssueRenderer = this.createIssueGroup(matchingBay, issueDetails);

      if (matchingIssueRenderer) {
        this.issueRenderers[`${asset._id}`] = matchingIssueRenderer;
        this.issues[`${asset._id}`] = matchingIssueRenderer.layoutIssues();
      } else {
        delete this.issueRenderers[`${asset._id}`];
        delete this.issues[`${asset._id}`];
      }
    } else if (shouldDisplay) {
      const knownIssues = matchingIssueRenderer.allIssues();
      const foundInx = knownIssues.findIndex((f) => f._id === issueDetails._id);
      if (foundInx !== -1) {
        knownIssues[foundInx] = issueDetails;
      } else {
        knownIssues.push(issueDetails);
      }

      matchingIssueRenderer.updateIssues(knownIssues);
      this.updateExistingIssueGroupWithFilteredIssues(matchingIssueRenderer);
    } else {
      this.removeIssues([issueDetails]);
    }
  }

  private createRepairGroup(matchingBay: LayoutBay, repair: Repair): RepairLayoutRenderer {
    if (!matchingBay) {
      console.warn(`Found no bay for repair.`, repair.upright);
      return null;
    }

    let top = matchingBay.y;
    let left = matchingBay.x;

    if (`${repair.upright._id}` === matchingBay.nextUpright.assetId) {
      top = matchingBay.nextUpright.y;
      left = matchingBay.nextUpright.x;
    }

    return new RepairLayoutRenderer(this.ctx, { x: left, y: top, repairs: [repair], assetId: `${repair.upright._id}`, size: DEFAULT_REPAIR_CFG.size });
  }

  private updateRepairSnap(repairDetails: ChangeTyped<Repair>): void {
    const asset = repairDetails.upright;

    let matchingRepairRenderer = this.repairRenderers[`${asset._id}`];
    const shouldDisplay = repairDetails.changeType !== 'removed';

    if (!matchingRepairRenderer && shouldDisplay) {
      const matchingBay = this.bays[`${asset._id}`];
      matchingRepairRenderer = this.createRepairGroup(matchingBay, repairDetails);

      if (matchingRepairRenderer) {
        this.repairRenderers[`${asset._id}`] = matchingRepairRenderer;
        this.repairs[`${asset._id}`] = matchingRepairRenderer.layoutRepairs();
      } else {
        delete this.repairRenderers[`${asset._id}`];
        delete this.repairs[`${asset._id}`];
      }
    } else if (shouldDisplay) {
      const knownRepairs = matchingRepairRenderer.allRepairs();
      const foundInx = knownRepairs.findIndex((f) => f._id === repairDetails._id);
      if (foundInx !== -1) {
        knownRepairs[foundInx] = repairDetails;
      } else {
        knownRepairs.push(repairDetails);
      }

      matchingRepairRenderer.updateRepairs(knownRepairs);
    } else {
      this.removeRepairs([repairDetails]);
    }
  }

  private removeRepair(matching: RepairLayoutRenderer, repair: Repair): void {
    const knownRepairs = matching.allRepairs();
    const repairInx = knownRepairs.findIndex((di) => di._id === repair._id);
    if (repairInx !== -1) {
      knownRepairs.splice(repairInx, 1);
      matching.updateRepairs(knownRepairs);

      if (knownRepairs.length === 0) {
        delete this.repairRenderers[`${repair.upright._id}`];

        if (this.selectedRepair?.assetId === `${repair.upright._id}`) {
          this.clearSelection(false);
        }
      }
    }
  }
  private removeIssue(matching: IssueLayoutRenderer, issue: Issue): void {
    const knownIssues = matching.allIssues();
    const issueInx = knownIssues.findIndex((di) => di._id === issue._id);
    if (issueInx !== -1) {
      knownIssues.splice(issueInx, 1);
      matching.updateIssues(knownIssues);

      if (knownIssues.length === 0) {
        delete this.issueRenderers[`${issue.asset._id}`];

        if (this.selectedIssue?.assetId === `${issue.asset._id}`) {
          this.clearSelection(false);
        }
      } else {
        this.updateExistingIssueGroupWithFilteredIssues(matching);
      }
    }
  }

  private updateExistingIssueGroupWithFilteredIssues(renderer: IssueLayoutRenderer): void {
    const knownIssues = renderer.allIssues();
    const filtered = knownIssues.reduce(
      (p, c) => {
        const listPriorityFilters = this.model.priorityFilter;
        if (filterLayoutIssue(c, this.model)) {
          p.issues.push(c);
          if (listPriorityFilters.length == 0) {
            p.weight += c.riskLevel?.weight || 0;
          } else {
            if (p.weight < c.riskLevel?.weight) {
              p.weight = c.riskLevel?.weight;
            }
          }
        }

        return p;
      },
      { issues: [] as Issue[], weight: 0 },
    );

    const risk = FirebaseIssueCfgService.riskFromWeight(filtered.weight, this.model.priorities);
    renderer.updateFill(this.model.issueCfg.colors[risk.description.toUpperCase()]);
    renderer.updateFilteredIssues(filtered.issues);
  }

  zoomOnSystems(systems: System2d[]): void {
    if (!systems || systems.length === 0) {
      return;
    }

    // Gather all bays from the systems
    const allBays: LayoutBay[] = [];
    for (const system of systems) {
      const renderer = this.systemRenderers[`S${system.number}`];
      if (renderer) {
        allBays.push(...renderer.layoutBays);
      }
    }

    if (allBays.length === 0) {
      return;
    }

    // Calculate bounding box for all bays
    let minX = Number.MAX_VALUE,
      minY = Number.MAX_VALUE;
    let maxX = -Number.MAX_VALUE,
      maxY = -Number.MAX_VALUE;

    for (const bay of allBays) {
      minX = Math.min(minX, bay.x);
      minY = Math.min(minY, bay.y);
      maxX = Math.max(maxX, bay.x + bay.w);
      maxY = Math.max(maxY, bay.y + bay.h);
    }

    const systemsWidth = maxX - minX;
    const systemsHeight = maxY - minY;

    const effectiveWidth = Math.max(systemsWidth, this.renderedBounds.width * 0.1);
    const effectiveHeight = Math.max(systemsHeight, this.renderedBounds.height * 0.1);

    // Calculate zoom to fit all systems
    const zoomX = this.canvasWidth() / effectiveWidth;
    const zoomY = this.canvasHeight() / effectiveHeight;
    const targetZoom = Math.min(zoomX, zoomY) / 1.1;

    // Center on all systems
    const centerX = minX + systemsWidth / 2;
    const centerY = minY + systemsHeight / 2;
    super.applyZoom({ x: centerX, y: centerY }, targetZoom);
  }

  private zoomOnObject(target: LayoutBay | LayoutIssues): void {
    if (!target) {
      return;
    }

    let targetWidth, targetHeight;

    if ('radius' in target) {
      targetWidth = targetHeight = target.radius * 2;
    } else {
      targetWidth = target.w;
      targetHeight = target.h;
    }

    let targetZoom = this.canvasWidth() / targetWidth;
    if (targetHeight > targetWidth) {
      targetZoom = this.canvasHeight() / targetHeight;
    }

    targetZoom /= 10;
    const x = target.x + targetWidth / 2;
    const y = target.y + targetHeight / 2;
    super.applyZoom({ x, y }, targetZoom);
  }

  private updateBoundsInModel(): void {
    this.model.warehouseBounds = this.renderedBounds;
    this.model.canvasBounds.width = this.canvasWidth();
    this.model.canvasBounds.height = this.canvasHeight();
  }

  protected updateDisplayPrecision(): void {
    if (!this.model) {
      return;
    }

    super.updateDisplayPrecision();

    if (this.model.displayPrecision !== this.renderingCtx.precision) {
      this.model.displayPrecision = this.renderingCtx.precision;
    }

    const pxOnCanvas = this.renderedBounds.width * this.renderingCtx.sx;
    this.model.zoomState.canReset = this.renderingCtx.sx !== this.defaultZoom || !isEqual(this.renderingCtx, this.defaultRenderingCtx);

    const canvasWidth = this.canvasWidth();
    this.model.zoomState.canZoomOut = pxOnCanvas > canvasWidth;
    this.model.zoomState.canZoomIn = pxOnCanvas < canvasWidth * 10;
  }
}
